// Déclaration de TVA déposée (26/10/2026) : l'abonné enregistre, pour le mois
// affiché dans la Synthèse TVA, la déclaration qu'il a déposée (date de dépôt,
// montants du formulaire). Le mois devient « déclaré ». Annulation réservée à
// l'administrateur, motif obligatoire.
import { useCallback, useEffect, useState } from "react";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const eur = (n) => `${Number(n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const frDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const today = () => new Date().toISOString().slice(0, 10);
const num = (v) => Number(String(v).replace(/\s/g, "").replace(",", "."));

// Liste repliable des lignes concernées par un point bloquant ou un avertissement
function PrereqItem({ p, color, onNavigate }) {
  return (
    <div style={{ color, marginTop: 4 }}>
      • {p.message}
      {p.page && onNavigate && p.page !== "synthesetva" && <button className="btn" style={{ marginLeft: 8, padding: "2px 8px", fontSize: 12 }} onClick={() => onNavigate(p.page)}>Ouvrir →</button>}
      {(p.items || []).length > 0 && p.items[0].label !== undefined && (
        <details style={{ marginLeft: 12, color: "var(--text-muted)" }}>
          <summary style={{ cursor: "pointer", fontSize: 12 }}>Voir les lignes ({p.items.length})</summary>
          {p.items.slice(0, 50).map(x => <div key={x.id} style={{ fontSize: 12 }}>{frDate(x.date)} — {x.label} — {eur(x.amount)}</div>)}
          {p.items.length > 50 && <div style={{ fontSize: 12 }}>… et {p.items.length - 50} autre(s)</div>}
        </details>
      )}
    </div>
  );
}

export default function VatDeclarationPanel({ companyId, month, summary, onNavigate }) {
  const { user } = useAuth();
  const canRecord = ["admin", "comptable"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [cancelReason, setCancelReason] = useState(null); // texte du motif pendant l'annulation
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (companyId) api.vatSummary.declarations(companyId).then(l => setList(Array.isArray(l) ? l : [])).catch(e => setError(e.message));
  }, [companyId]);
  useEffect(() => { load(); setForm(null); setCancelReason(null); setError(""); }, [load, month]);

  const filed = list.find(d => d.period_start.slice(0, 7) === month && ["submitted", "paid"].includes(d.status));
  const cancelled = list.filter(d => d.period_start.slice(0, 7) === month && d.cancelled_at);
  const monthEnded = month < today().slice(0, 7); // mois antérieur au mois en cours

  // Conditions préalables (B5, 02/11/2026) : bouton grisé tant qu'un point bloque
  const [prereq, setPrereq] = useState(null);
  useEffect(() => {
    setPrereq(null);
    if (companyId && monthEnded && !filed) api.vatSummary.declarationPrerequisites(companyId, month).then(setPrereq).catch(e => setError(e.message));
  }, [companyId, month, monthEnded, filed]);
  const blocked = !prereq || !prereq.ready;

  const openForm = () => {
    const collected = (summary?.collected?.total || 0) + (summary?.autoliquidation?.due || 0);
    const deductible = summary?.totals?.tva_deductible || 0;
    setForm({ filed_on: today(), tva_collectee: collected.toFixed(2), tva_deductible: deductible.toFixed(2), tva_nette: (collected - deductible).toFixed(2) });
    setError("");
  };
  const submit = async () => {
    setBusy(true); setError("");
    try {
      await api.vatSummary.recordDeclaration(companyId, { month, filed_on: form.filed_on, tva_collectee: num(form.tva_collectee), tva_deductible: num(form.tva_deductible), tva_nette: num(form.tva_nette) });
      setForm(null); load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const cancel = async () => {
    setBusy(true); setError("");
    try { await api.vatSummary.cancelDeclaration(companyId, filed.id, cancelReason); setCancelReason(null); load(); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>Déclaration déposée</div>
      {error && <div style={{ background: "#e24b4a22", color: "#e24b4a", padding: "8px 12px", borderRadius: 8, marginBottom: 8, fontSize: 13 }}>{error}</div>}
      {filed ? (
        <div style={{ fontSize: 13 }}>
          <div style={{ color: "#166534", fontWeight: 600 }}>✓ Déclaré le {frDate(filed.submitted_at)}{filed.declared_by_name ? ` — enregistré par ${filed.declared_by_name}` : ""}</div>
          <div style={{ color: "var(--text-muted)", marginTop: 4 }}>
            TVA collectée {eur(filed.tva_collectee)} · TVA déductible {eur(filed.tva_deductible)} · {filed.tva_nette >= 0 ? `TVA nette ${eur(filed.tva_nette)}` : `Crédit ${eur(-filed.tva_nette)}`}.
            La TVA de ce mois ne change plus : une TVA déductible qui le concernerait est reportée au premier mois ouvert ; une TVA collectée est refusée.
          </div>
          {isAdmin && cancelReason === null && <button className="btn" style={{ marginTop: 8 }} onClick={() => setCancelReason("")}>Annuler l'enregistrement</button>}
          {isAdmin && cancelReason !== null && (
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 8 }}>
              <input className="input" placeholder="Motif de l'annulation (obligatoire)" value={cancelReason} onChange={e => setCancelReason(e.target.value)} style={{ flex: 1, minWidth: 240 }} />
              <button className="btn primary" disabled={busy || cancelReason.trim().length < 5} onClick={cancel}>Confirmer l'annulation</button>
              <button className="btn" disabled={busy} onClick={() => setCancelReason(null)}>Retour</button>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: 13 }}>
          <div style={{ color: "var(--text-muted)" }}>
            {monthEnded ? "Une fois votre déclaration déposée, enregistrez-la ici : la TVA de ce mois ne changera plus." : "Le mois doit être terminé pour enregistrer sa déclaration."}
          </div>
          {monthEnded && prereq && prereq.blockers.length > 0 && (
            <div style={{ background: "#e24b4a11", border: "1px solid #e24b4a55", borderRadius: 8, padding: "8px 12px", marginTop: 8 }}>
              <div style={{ fontWeight: 600, color: "#b91c1c" }}>À terminer avant d'enregistrer la déclaration de ce mois (sinon la TVA de ces opérations serait ensuite refusée) :</div>
              {prereq.blockers.map(p => <PrereqItem key={p.code} p={p} color="#b91c1c" onNavigate={onNavigate} />)}
            </div>
          )}
          {monthEnded && prereq && prereq.warnings.length > 0 && (
            <div style={{ background: "#f59e0b11", border: "1px solid #f59e0b55", borderRadius: 8, padding: "8px 12px", marginTop: 8 }}>
              <div style={{ fontWeight: 600, color: "#b45309" }}>À savoir (n'empêche pas l'enregistrement) :</div>
              {prereq.warnings.map(p => <PrereqItem key={p.code} p={p} color="#b45309" onNavigate={onNavigate} />)}
            </div>
          )}
          {canRecord && monthEnded && !form && <button className="btn primary" style={{ marginTop: 8 }} disabled={blocked} title={blocked ? "Terminez d'abord les points ci-dessus" : undefined} onClick={openForm}>✓ Enregistrer ma déclaration déposée</button>}
          {form && (
            <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 14px", marginTop: 8 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 8 }}>
                {[["filed_on", "Date de dépôt", "date"], ["tva_collectee", "TVA collectée (€)"], ["tva_deductible", "TVA déductible (€)"], ["tva_nette", "TVA nette (€, négative = crédit)"]].map(([k, l, type]) => (
                  <label key={k} style={{ fontSize: 12 }}>{l}
                    <input className="input" type={type || "text"} inputMode={type ? undefined : "decimal"} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} style={{ width: "100%" }} />
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 6 }}>Montants repris de la synthèse : corrigez-les s'ils diffèrent du formulaire déposé.</div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button className="btn primary" disabled={busy} onClick={submit}>Enregistrer la déclaration</button>
                <button className="btn" disabled={busy} onClick={() => setForm(null)}>Annuler</button>
              </div>
            </div>
          )}
        </div>
      )}
      {cancelled.length > 0 && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8 }}>
          {cancelled.map(d => <div key={d.id}>Enregistrement du {frDate(d.submitted_at)} annulé le {frDate(d.cancelled_at)}{d.cancelled_by_name ? ` par ${d.cancelled_by_name}` : ""} : {d.cancel_reason}</div>)}
        </div>
      )}
    </div>
  );
}
